import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "../config";
import "../styles/Auth.css";
import "../styles/NewAuth.css";
import MainFooter from "../components/MainFooter";

const LOGO_SRC = "/eventfork_logo.svg";

/* ── Verification Status Screen ─────────────────────────────── */
const VerificationStatus = ({
  email, onResendVerification, onBackToSignup,
  remainingTime, maxResendAttempts, resendAttempts,
}) => {
  const [showResendError, setShowResendError] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleResend = async () => {
    if (resendAttempts >= maxResendAttempts) { setShowResendError(true); return; }
    setResendLoading(true);
    try {
      await onResendVerification();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error) {
      console.error("Resend failed:", error);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-wrapper">
          <div className="auth-branding">
            <div className="container">
              <h1>Almost <em>there!</em></h1>
              <p>
                Complete your registration by verifying your email.
                This helps us secure your account and keep you in the loop.
              </p>
              <ul className="features-list">
                <li><i className="fas fa-check" /> Secure account activation</li>
                <li><i className="fas fa-check" /> Instant access upon verification</li>
                <li><i className="fas fa-check" /> Priority customer support</li>
                <li><i className="fas fa-check" /> Full event management capabilities</li>
              </ul>
            </div>
          </div>

          <div className="auth-form-container">
            <div className="form-header">
              <img src={LOGO_SRC} alt="EventFork" />
              <h2>Check your inbox</h2>
              <p>We've sent a verification link to your email.</p>
            </div>

            <div className="verification-content">
              <div className="email-display">
                <strong>Email sent to</strong>
                <div className="email-address">{email}</div>
              </div>

              <div className="verification-steps">
                <h3>Next steps</h3>
                <ul>
                  <li>Check your inbox (and spam folder)</li>
                  <li>Click the verification link in the email</li>
                  <li>Your account activates immediately</li>
                </ul>
              </div>

              <div className="timer-display">
                <p>
                  Waiting for verification — {Math.floor(remainingTime / 60)}:
                  {(remainingTime % 60).toString().padStart(2, "0")} remaining
                </p>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${(remainingTime / 180) * 100}%` }} />
                </div>
              </div>

              {showResendError && (
                <div className="error-message">
                  Maximum resend attempts reached. Please contact support.
                </div>
              )}
              {resendSuccess && (
                <div className="success-message">New verification email sent!</div>
              )}

              <div className="verification-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleResend}
                  disabled={resendLoading || resendAttempts >= maxResendAttempts}
                >
                  {resendLoading
                    ? "Sending…"
                    : `Resend Email (${resendAttempts}/${maxResendAttempts})`}
                </button>
                <button type="button" className="btn btn-outline" onClick={onBackToSignup}>
                  ← Back to Sign Up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Verification Failed Screen ─────────────────────────────── */
const VerificationFailed = ({
  onResendVerification, onBackToSignup, maxResendAttempts, resendAttempts,
}) => {
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showResendError, setShowResendError] = useState(false);

  const handleResend = async () => {
    if (resendAttempts >= maxResendAttempts) { setShowResendError(true); return; }
    setResendLoading(true);
    try {
      await onResendVerification();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error) {
      console.error("Resend failed:", error);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-wrapper">
          <div className="auth-branding">
            <div className="container">
              <h1>Let's get you <em>verified</em></h1>
              <p>
                Verification links expire after 24 hours, but we'll get you
                a fresh one right away.
              </p>
              <ul className="features-list">
                <li><i className="fas fa-shield-alt" /> Account security first</li>
                <li><i className="fas fa-clock" /> Links valid for 24 hours</li>
                <li><i className="fas fa-sync-alt" /> Easy resend available</li>
                <li><i className="fas fa-headset" /> Support team ready to help</li>
              </ul>
            </div>
          </div>

          <div className="auth-form-container">
            <div className="form-header">
              <img src={LOGO_SRC} alt="EventFork" />
              <h2>Verification failed</h2>
              <p>The link is invalid or has expired.</p>
            </div>

            <div className="verification-content">
              <div className="error-description">
                <p>This can happen when:</p>
                <ul className="error-list">
                  <li>The link was already used</li>
                  <li>The link expired (valid for 24 hours)</li>
                  <li>You clicked an older verification email</li>
                </ul>
              </div>

              <div className="solutions">
                <h3>Fix it in one step</h3>
                {showResendError ? (
                  <div className="error-message">
                    Maximum resend attempts reached. Please contact our support team.
                  </div>
                ) : (
                  <>
                    {resendSuccess && (
                      <div className="success-message">New verification email sent!</div>
                    )}
                    <div className="solution-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleResend}
                        disabled={resendLoading || resendAttempts >= maxResendAttempts}
                      >
                        {resendLoading
                          ? "Sending…"
                          : `Send New Link (${resendAttempts}/${maxResendAttempts})`}
                      </button>
                      <button type="button" className="btn btn-outline" onClick={onBackToSignup}>
                        ← Back to Sign Up
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main SignUp Component ───────────────────────────────────── */
const SignUp = ({ setUser }) => {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", password: "", confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [verificationStage, setVerificationStage] = useState("signup");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [remainingTime, setRemainingTime] = useState(180);
  const [verificationChecked, setVerificationChecked] = useState(false);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [verificationToken, setVerificationToken] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const navigate = useNavigate();
  const checkIntervalRef = useRef(null);

  const MAX_RESEND_ATTEMPTS = 3;
  const CHECK_INTERVAL = 5000;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      if (/^\d*$/.test(value) && value.length <= 10)
        setFormData({ ...formData, [name]: value });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError("");

  if (formData.password !== formData.confirmPassword) {
    setError("Passwords do not match");
    return;
  }

  const password = formData.password;

  if (password.length < 6) {
    setError("Password must contain at least 6 characters");
    return;
  }

  if (!/[a-z]/.test(password)) {
    setError("Password must contain at least 1 lowercase letter");
    return;
  }

  if (!/[0-9]/.test(password)) {
    setError("Password must contain at least 1 number");
    return;
  }

  if (!/[^A-Za-z0-9\s]/.test(password)) {
    setError("Password must contain at least 1 special character");
    return;
  }

  if (/\s/.test(password)) {
    setError("Password should not contain spaces");
    return;
  }

  if (!agreeToTerms) {
    setError("You must agree to the Terms of Service and Privacy Policy");
    return;
  }

  if (formData.phone.length !== 10) {
    setError("Phone number must be exactly 10 digits");
    return;
  }
    if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      setError("Please enter a valid 10-digit phone number starting with 6–9"); return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setVerificationEmail(formData.email);
        setVerificationToken(data.verificationToken || data.token);
        localStorage.setItem("pendingVerification", JSON.stringify({
          email: formData.email,
          token: data.verificationToken || data.token,
          attempts: 0,
        }));
        setVerificationStage("verifying");
        startVerificationCheck();
      } else {
        setError(data.message || "Sign up failed");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startVerificationCheck = () => {
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    const timerInterval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) { clearInterval(timerInterval); handleVerificationTimeout(); return 0; }
        return prev - 1;
      });
    }, 1000);
    checkIntervalRef.current = setInterval(checkVerificationStatus, CHECK_INTERVAL);
  };

  const checkVerificationStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/confirmationstatus:${verificationEmail}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.verified) {
          clearInterval(checkIntervalRef.current);
          if (data.token) localStorage.setItem("token", data.token);
          if (data.user) { localStorage.setItem("user", JSON.stringify(data.user)); setUser(data.user); }
          localStorage.removeItem("pendingVerification");
          navigate("/");
        } else if (data.error) {
          handleVerificationFailed();
        }
      }
    } catch (error) {
      console.error("Verification check failed:", error);
    }
  };

  const handleVerificationTimeout = () => {
    clearInterval(checkIntervalRef.current);
    setVerificationStage("failed");
    setVerificationChecked(true);
  };

  const handleVerificationFailed = () => {
    clearInterval(checkIntervalRef.current);
    setVerificationStage("failed");
    setVerificationChecked(true);
  };

  const handleResendVerification = async () => {
    if (resendAttempts >= MAX_RESEND_ATTEMPTS) throw new Error("Maximum resend attempts reached");
    const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: verificationEmail, token: verificationToken }),
    });
    if (response.ok) {
      const data = await response.json();
      setResendAttempts((prev) => {
        const newAttempts = prev + 1;
        const pendingData = JSON.parse(localStorage.getItem("pendingVerification") || "{}");
        pendingData.attempts = newAttempts;
        localStorage.setItem("pendingVerification", JSON.stringify(pendingData));
        return newAttempts;
      });
      setRemainingTime(180);
      startVerificationCheck();
      return data;
    } else {
      throw new Error("Failed to resend verification email");
    }
  };

  const handleBackToSignup = () => {
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    localStorage.removeItem("pendingVerification");
    setVerificationStage("signup");
    setVerificationEmail("");
    setRemainingTime(180);
    setVerificationChecked(false);
    setResendAttempts(0);
  };

  useEffect(() => {
    const pendingVerification = localStorage.getItem("pendingVerification");
    if (pendingVerification) {
      const pendingData = JSON.parse(pendingVerification);
      setVerificationEmail(pendingData.email);
      setVerificationToken(pendingData.token);
      setResendAttempts(pendingData.attempts || 0);
      setVerificationStage("verifying");
      startVerificationCheck();
    }
    return () => { if (checkIntervalRef.current) clearInterval(checkIntervalRef.current); };
  }, []);

  if (verificationStage === "verifying") {
    return (
      <VerificationStatus
        email={verificationEmail}
        onResendVerification={handleResendVerification}
        onBackToSignup={handleBackToSignup}
        remainingTime={remainingTime}
        maxResendAttempts={MAX_RESEND_ATTEMPTS}
        resendAttempts={resendAttempts}
      />
    );
  }

  if (verificationStage === "failed") {
    return (
      <VerificationFailed
        onResendVerification={handleResendVerification}
        onBackToSignup={handleBackToSignup}
        maxResendAttempts={MAX_RESEND_ATTEMPTS}
        resendAttempts={resendAttempts}
      />
    );
  }

  const passwordsMatch =
    formData.password && formData.confirmPassword &&
    formData.password === formData.confirmPassword;
  const passwordsMismatch =
    formData.password && formData.confirmPassword &&
    formData.password !== formData.confirmPassword;

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-wrapper">

          {/* ── LEFT BRANDING PANEL ── */}
          <div className="auth-branding">
            <div className="container">
              <h1>
                Join the <em>EventFork</em> community
              </h1>
              <p>
                Discover events in your city, connect with people who share
                your passions, and create experiences worth remembering.
              </p>
              <ul className="features-list">
                <li><i className="fas fa-check" /> Personalized recommendations</li>
                <li><i className="fas fa-check" /> One-click ticket purchasing</li>
                <li><i className="fas fa-check" /> Create and manage your events</li>
                <li><i className="fas fa-check" /> Connect with event enthusiasts</li>
              </ul>

              <div className="branding-tags">
                {["Music", "Tech", "Food & Drink", "Art", "Networking", "Wellness"].map((tag) => (
                  <span className="branding-tag" key={tag}>{tag}</span>
                ))}
              </div>
            </div>
          </div>

          {/* ── RIGHT FORM PANEL ── */}
          <div className="auth-form-container">
            <div className="form-header">
              <img src={LOGO_SRC} alt="EventFork" />
              <h2>Create your account</h2>
              <p>Start creating and discovering events today.</p>
            </div>

            <div className="auth-toggle">
              <Link to="/signin" className="toggle-btn">Sign In</Link>
              <Link to="/signup" className="toggle-btn active">Sign Up</Link>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">Full Name *</label>
                <input
                  type="text" id="name" name="name" className="form-input"
                  value={formData.name} onChange={handleChange}
                  required placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address *</label>
                <input
                  type="email" id="email" name="email" className="form-input"
                  value={formData.email} onChange={handleChange}
                  required placeholder="you@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone Number</label>
                <input
                  type="tel" id="phone" name="phone" className="form-input"
                  value={formData.phone} onChange={handleChange}
                  placeholder="10-digit mobile number" maxLength={10}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">Password *</label>
                <div className="password-container">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    id="password" name="password" className="form-input"
                    value={formData.password} onChange={handleChange}
                    required placeholder="Create a strong password" minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle password-toggle-span"
                    onClick={() => setPasswordVisible((v) => !v)}
                  >
                    {passwordVisible ? "Hide" : "Show"}
                  </button>
                </div>
                <small className="password-hint">Minimum 6 characters</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">Confirm Password *</label>
                <div className="password-container">
                  <input
                    type={confirmPasswordVisible ? "text" : "password"}
                    id="confirmPassword" name="confirmPassword" className="form-input"
                    value={formData.confirmPassword} onChange={handleChange}
                    required placeholder="Re-enter your password" minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle password-toggle-span"
                    onClick={() => setConfirmPasswordVisible((v) => !v)}
                  >
                    {confirmPasswordVisible ? "Hide" : "Show"}
                  </button>
                </div>
                {passwordsMismatch && <small className="error-text">Passwords do not match</small>}
                {passwordsMatch && <small className="success-text">Passwords match ✓</small>}
              </div>

              <div className="form-group">
                <div className="checkbox-container">
                  <input
                    type="checkbox" id="agreeToTerms" name="agreeToTerms"
                    checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)}
                    required
                  />
                  <label htmlFor="agreeToTerms" className="checkbox-label">
                    I agree to the{" "}
                    <Link to="/terms" className="terms-link">Terms of Service</Link>{" "}
                    and{" "}
                    <Link to="/privacy" className="terms-link">Privacy Policy</Link> *
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary1"
                disabled={loading || passwordsMismatch || !agreeToTerms}
              >
                {loading ? "Creating Account…" : "Create Account"}
              </button>
            </form>

            <div className="auth-redirect">
              Already have an account?{" "}
              <Link to="/signin" className="auth-link">Sign In</Link>
            </div>
          </div>
        </div>
      </div>
      <MainFooter />
    </div>
  );
};

export default SignUp;
